import * as THREE from "three";

/**
 * Решающий удар в 3D (описание консультанта 05.11.2026: «след на конечностях… на попадании кольцо ударной волны»):
 * пока мир замедлен — за кистями и стопами обоих бойцов тянется светящийся след, сужаясь к хвосту; на попадании из
 * точки удара расходится кольцо волны и гаснет. Только картинка: читает положения костей и точку удара.
 */

/** Кости, за которыми тянется след. */
const LIMBS = ["mixamorigRightHand", "mixamorigLeftHand", "mixamorigRightFoot", "mixamorigLeftFoot"] as const;
/** Точек следа (кадров экрана) и его ширина у конечности, пиксели мира. */
const SAMPLES = 14;
const WIDTH = 9;
const TRAIL_COLOR = new THREE.Color(1, 0.78, 0.45);
/** Кольцо: кадров картинки (часы замедления) до конца, радиус в конце, толщина. */
const RING_FRAMES = 13;
const RING_RADIUS = 130;
const RING_COLOR = 0xfff1d6;

/** Точка удара для кольца: где (пиксели мира, y — вверх от пола) и сколько кадров картинки прошло. */
export interface RingView {
  readonly x: number;
  readonly y: number;
  readonly age: number;
}

class Trail {
  readonly mesh: THREE.Mesh;
  private readonly points: THREE.Vector3[] = [];
  private readonly positions = new Float32Array(SAMPLES * 2 * 3);
  private readonly colors = new Float32Array(SAMPLES * 2 * 4);
  private readonly geometry = new THREE.BufferGeometry();
  private readonly positionAttribute = new THREE.BufferAttribute(this.positions, 3);
  private readonly colorAttribute = new THREE.BufferAttribute(this.colors, 4);

  constructor(scene: THREE.Scene) {
    this.geometry.setAttribute("position", this.positionAttribute);
    this.geometry.setAttribute("color", this.colorAttribute);
    const index: number[] = [];
    for (let i = 0; i < SAMPLES - 1; i++) {
      const a = i * 2;
      index.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    this.geometry.setIndex(index);
    const material = new THREE.MeshBasicMaterial({
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    this.mesh = new THREE.Mesh(this.geometry, material);
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
    scene.add(this.mesh);
  }

  /** Новая точка следа; strength 0..1 — насколько ярок; view — куда смотрит камера (лента повёрнута к ней). */
  update(point: THREE.Vector3 | null, strength: number, view: THREE.Vector3): void {
    if (!point || strength <= 0.02) {
      this.points.length = 0;
      this.mesh.visible = false;
      return;
    }
    this.points.unshift(point.clone());
    if (this.points.length > SAMPLES) this.points.length = SAMPLES;
    const count = this.points.length;
    this.mesh.visible = count > 1;
    const side = new THREE.Vector3();
    const along = new THREE.Vector3();
    for (let i = 0; i < SAMPLES; i++) {
      const p = this.points[Math.min(i, count - 1)] ?? point;
      const next = this.points[Math.min(i + 1, count - 1)] ?? p;
      along.subVectors(p, next);
      if (along.lengthSq() < 1e-6) along.set(0, 1, 0);
      side.crossVectors(along, view).normalize();
      const fade = i < count ? 1 - i / SAMPLES : 0;
      side.multiplyScalar((WIDTH / 2) * fade);
      this.positions.set([p.x + side.x, p.y + side.y, p.z + side.z, p.x - side.x, p.y - side.y, p.z - side.z], i * 6);
      const alpha = 0.55 * strength * fade * fade;
      const color = [TRAIL_COLOR.r, TRAIL_COLOR.g, TRAIL_COLOR.b, alpha];
      this.colors.set([...color, ...color], i * 8);
    }
    this.positionAttribute.needsUpdate = true;
    this.colorAttribute.needsUpdate = true;
  }
}

export class DecisiveFx3D {
  private readonly trails: Trail[][];
  private readonly ring: THREE.Mesh;
  private readonly ringMaterial = new THREE.MeshBasicMaterial({
    color: RING_COLOR,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });
  private readonly point = new THREE.Vector3();

  constructor(scene: THREE.Scene) {
    this.trails = [0, 1].map(() => LIMBS.map(() => new Trail(scene)));
    this.ring = new THREE.Mesh(new THREE.RingGeometry(0.93, 1, 72), this.ringMaterial);
    this.ring.visible = false;
    scene.add(this.ring);
  }

  /** Кадр: модели бойцов, сила замедления 0..1, кольцо (если было попадание), камера. */
  update(roots: readonly THREE.Object3D[], strength: number, ring: RingView | null, camera: THREE.Camera): void {
    const view = new THREE.Vector3();
    camera.getWorldDirection(view);
    roots.forEach((root, side) => {
      LIMBS.forEach((limb, index) => {
        const bone = root.visible ? root.getObjectByName(limb) : undefined;
        const point = bone ? bone.getWorldPosition(this.point) : null;
        this.trails[side]?.[index]?.update(point, strength, view);
      });
    });
    const u = ring ? ring.age / RING_FRAMES : 1;
    this.ring.visible = ring !== null && u >= 0 && u < 1;
    if (!ring || !this.ring.visible) return;
    // волна расходится быстро и тормозит, тоньше и прозрачнее к концу; плоскость кольца — к камере
    const grow = 1 - (1 - u) * (1 - u) * (1 - u);
    this.ring.position.set(ring.x, ring.y, 0);
    this.ring.quaternion.copy(camera.quaternion);
    this.ring.scale.setScalar(Math.max(1, RING_RADIUS * grow));
    this.ringMaterial.opacity = 0.7 * (1 - u) * (1 - u);
  }
}
